import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Plug, Smartphone, ShieldCheck, Eye, Download, Bell, Trophy, Target, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { DiscountCodeInput, type AppliedDiscount } from "@/components/DiscountCodeInput";
import { supabase } from "@/integrations/supabase/client";

type Cycle = "monthly" | "yearly";

const SOLO_FEATURES = [
  "Unlimited deals & activities",
  "Quick Log + Next Step enforcement",
  "Push alerts: Hot deals, 24h stall, red CSI",
  "Personal leaderboard & scoring",
  "PWA — install on phone, works offline",
];
const DEALERSHIP_FEATURES = [
  "Everything in Solo, for every rep",
  "Manager dashboard & Pilot Health",
  "Team leaderboard & rep management",
  "Invite reps via email",
  "Multi-rep CSI & customer feedback feed",
  "Priority pilot support",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated, userId, userName } = useAuth();
  const { openCheckout, loading } = usePaddleCheckout();
  const { subscription, hasAccess } = useSubscription();
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [seats, setSeats] = useState(3);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [discount, setDiscount] = useState<AppliedDiscount | null>(null);

  const startCheckout = async (plan: "solo" | "dealership") => {
    if (!isAuthenticated) {
      navigate("/login?next=/pricing");
      return;
    }
    const priceId =
      plan === "solo"
        ? cycle === "monthly" ? "solo_monthly" : "solo_yearly"
        : cycle === "monthly" ? "dealership_monthly" : "dealership_yearly";
    const quantity = plan === "dealership" ? Math.max(1, Math.min(100, seats)) : 1;
    setPendingPlan(plan);
    try {
      // Pull billing details if user has saved any
      let billing: { businessName?: string; taxId?: string; taxIdCountry?: string } = {};
      if (userId) {
        const { data } = await supabase
          .from("profiles")
          .select("billing_email, tax_id, tax_id_country, full_name")
          .eq("id", userId)
          .maybeSingle();
        const p = data as any;
        if (p?.tax_id) {
          billing = {
            businessName: p.full_name || undefined,
            taxId: p.tax_id,
            taxIdCountry: p.tax_id_country || undefined,
          };
        }
      }
      await openCheckout({
        priceId,
        quantity,
        customData: { userId: userId || "" },
        successUrl: `${window.location.origin}/checkout/success`,
        discountCode: discount?.code,
        ...billing,
      });
    } catch (e) {
      toast.error("Could not open checkout", { description: (e as Error).message });
    } finally {
      setPendingPlan(null);
    }
  };

  const soloMonthly = cycle === "yearly" ? "$24" : "$29";
  const dealershipMonthly = cycle === "yearly" ? "$41" : "$49";
  const soloSavings = (29 - 24) * 12; // $60
  const dealershipSavingsPerSeat = (49 - 41) * 12; // $96
  const soloAnnualNote =
    cycle === "yearly" ? `Save $${soloSavings}/year with annual billing` : "Billed monthly";
  const dealershipAnnualNote =
    cycle === "yearly"
      ? `Save $${dealershipSavingsPerSeat}/seat/year with annual billing`
      : "Per seat, billed monthly";
  const dealershipMonthlyNum = parseInt(dealershipMonthly.replace("$", ""));
  const teamMonthlyTotal = dealershipMonthlyNum * seats;
  const teamYearlySavings = dealershipSavingsPerSeat * seats;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />
      <nav className="border-b border-border/50 backdrop-blur-sm sticky top-0 z-40 bg-background/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" aria-label="Home"><Logo variant="full" className="h-12 sm:h-14 object-contain" /></Link>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard"><Button variant="ghost" size="sm">Dashboard</Button></Link>
            ) : (
              <Link to="/login"><Button variant="ghost" size="sm">Sign In</Button></Link>
            )}
          </div>
        </div>
      </nav>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10 text-center">
        <Badge className="mb-4">14-day free trial · Cancel anytime</Badge>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4">
          Built for the floor. <span className="text-primary">Priced for closers.</span>
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
          {isAuthenticated && userName ? `Welcome back, ${userName.split(" ")[0]}. ` : ""}
          Built for dealerships that need visibility, accountability, and faster follow-up execution.
          Start free for 14 days — keep access through the end of your paid period whenever you cancel.
        </p>

        <div className="inline-flex rounded-full border border-border bg-card p-1 mb-12">
          <button
            onClick={() => setCycle("monthly")}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition ${cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >Monthly</button>
          <button
            onClick={() => setCycle("yearly")}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition ${cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >Yearly · save 2 months</button>
        </div>

        <div className="mb-2">
          <DiscountCodeInput onApplied={setDiscount} />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid md:grid-cols-2 gap-6 items-start">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Card className="h-full border-border/40 bg-card/50">
            <CardHeader className="pb-3">
              <div className="text-sm font-medium text-muted-foreground/80 uppercase tracking-wide">Solo</div>
              <div className="text-xs text-muted-foreground mt-0.5">Best for independent reps</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-5xl font-bold tabular-nums">{soloMonthly}</span>
                <span className="text-muted-foreground">/ month</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{soloAnnualNote}</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">For independent reps who want their own pipeline & alerts.</p>
              <ul className="space-y-2.5">
                {SOLO_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                size="default"
                variant="outline"
                onClick={() => startCheckout("solo")}
                disabled={loading || (hasAccess && subscription?.product_id === "solo_plan")}
              >
                {pendingPlan === "solo" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {hasAccess && subscription?.product_id === "solo_plan" ? "Current plan" : "Start 14-day trial"}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
          <Card className="h-full border-primary/60 bg-gradient-to-b from-card to-card/60 relative overflow-hidden md:scale-[1.06] md:-mt-2 shadow-2xl shadow-primary/20 ring-1 ring-primary/10">
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] tracking-widest uppercase font-semibold py-0.5 px-2.5 rounded-bl-md">
              For teams
            </div>
            <CardHeader className="pb-3">
              <div className="text-sm font-semibold text-primary uppercase tracking-wide">Dealership</div>
              <div className="text-xs text-muted-foreground mt-0.5">Best for teams that need real-time floor visibility</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-5xl font-bold tabular-nums">{dealershipMonthly}</span>
                <span className="text-muted-foreground">/ seat / month</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{dealershipAnnualNote}</p>
              <p className="text-xs italic text-muted-foreground/80 mt-1">Less than one missed deal per month.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">For dealership teams. Manager dashboard + per-rep seats.</p>
              <ul className="space-y-2.5">
                {DEALERSHIP_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-lg border border-border/60 bg-background/40 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground whitespace-nowrap">Seats</label>
                  <Input
                    type="number" min={1} max={100} value={seats}
                    onChange={(e) => setSeats(parseInt(e.target.value) || 1)}
                    className="h-9 w-20 tabular-nums"
                  />
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {seats} {seats === 1 ? "seat" : "seats"} · ${teamMonthlyTotal.toLocaleString()}/month
                  </span>
                </div>
                {cycle === "yearly" && seats > 1 && (
                  <div className="text-xs text-primary/90 tabular-nums">
                    Team saves ${teamYearlySavings.toLocaleString()}/year on annual billing
                  </div>
                )}
              </div>
              <Button
                className="w-full"
                size="lg"
                onClick={() => startCheckout("dealership")}
                disabled={loading || (hasAccess && subscription?.product_id === "dealership_plan")}
              >
                {pendingPlan === "dealership" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {hasAccess && subscription?.product_id === "dealership_plan" ? "Current plan" : "Start 14-day trial"}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Enterprise trust strip */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="rounded-xl border border-border/50 bg-card/40 px-5 py-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
          {[
            { icon: Plug, label: "Works alongside your existing CRM" },
            { icon: Smartphone, label: "Mobile-first PWA" },
            { icon: ShieldCheck, label: "Secure cloud sync" },
            { icon: Eye, label: "Real-time manager visibility" },
            { icon: Download, label: "No installation required" },
          ].map(({ icon: Icon, label }, i, arr) => (
            <div key={label} className="flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5 text-primary/80" />
              <span>{label}</span>
              {i < arr.length - 1 && <span className="hidden md:inline text-border ml-4">·</span>}
            </div>
          ))}
        </div>
      </section>

      {/* Product mini previews */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">See what's inside</h2>
          <p className="text-sm text-muted-foreground mt-1">Real screens. Built for the floor.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Manager Dashboard */}
          <div className="rounded-xl border border-border/50 bg-card/60 overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/50">Manager Dashboard</div>
            <div className="p-3 space-y-2">
              <div className="grid grid-cols-2 gap-1.5">
                {["Active 42","Stalled 6","Sold 18","CSI 94"].map((t)=>(
                  <div key={t} className="rounded bg-background/60 border border-border/40 p-1.5 text-[10px] tabular-nums">{t}</div>
                ))}
              </div>
              <div className="space-y-1">
                {["Mike · Demo · 2m","Sarah · Follow-up · 12m","Alex · Stall 24h","Jen · Sold ✓"].map((r,i)=>(
                  <div key={i} className="flex justify-between text-[10px] text-muted-foreground border-b border-border/30 pb-0.5">
                    <span>{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Mobile Rep Dashboard */}
          <div className="rounded-xl border border-border/50 bg-card/60 overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/50">Rep · Today</div>
            <div className="p-3 space-y-1.5">
              {[{i:Bell,t:"Call Johnson · 10:30"},{i:Target,t:"Demo Patel · 1:00"},{i:Zap,t:"Send numbers · Lee"},{i:Bell,t:"Follow-up · Kim"}].map(({i:I,t},k)=>(
                <div key={k} className="flex items-center gap-1.5 text-[10px] rounded bg-background/60 border border-border/40 px-2 py-1.5">
                  <I className="w-3 h-3 text-primary" /><span>{t}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Quick Log */}
          <div className="rounded-xl border border-border/50 bg-card/60 overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/50">Quick Log</div>
            <div className="p-3 space-y-1.5">
              <div className="text-[10px] text-muted-foreground">Activity</div>
              <div className="rounded bg-background/60 border border-border/40 px-2 py-1.5 text-[10px]">Demo · Patel</div>
              <div className="text-[10px] text-muted-foreground pt-1">Next step</div>
              {["Call","Follow-up","Appointment","Close"].map((s,i)=>(
                <div key={s} className={`rounded px-2 py-1 text-[10px] border ${i===1?"border-primary bg-primary/10 text-primary":"border-border/40 bg-background/40"}`}>{s}</div>
              ))}
            </div>
          </div>
          {/* Leaderboard */}
          <div className="rounded-xl border border-border/50 bg-card/60 overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/50">Leaderboard</div>
            <div className="p-3 space-y-1.5">
              {[{r:1,n:"Sarah K.",s:984},{r:2,n:"Mike T.",s:872},{r:3,n:"Alex R.",s:801}].map(({r,n,s})=>(
                <div key={r} className="flex items-center justify-between rounded bg-background/60 border border-border/40 px-2 py-1.5 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <Trophy className={`w-3 h-3 ${r===1?"text-primary":"text-muted-foreground"}`} />
                    <span>{r}. {n}</span>
                  </div>
                  <span className="tabular-nums text-muted-foreground">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/50 py-10 text-xs text-muted-foreground">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="text-foreground text-sm font-semibold mb-3">Product</div>
            <ul className="space-y-2">
              <li><Link to="/#features" className="hover:text-foreground">Features</Link></li>
              <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
              <li><Link to="/#faq" className="hover:text-foreground">Roadmap</Link></li>
              <li><Link to="/login?tab=demo" className="hover:text-foreground">Demo</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-foreground text-sm font-semibold mb-3">Company</div>
            <ul className="space-y-2">
              <li><a href="mailto:hello@pullover.app" className="hover:text-foreground">Contact</a></li>
              <li><Link to="/#security" className="hover:text-foreground">Security</Link></li>
              <li><a href="mailto:support@pullover.app" className="hover:text-foreground">Support</a></li>
            </ul>
          </div>
          <div>
            <div className="text-foreground text-sm font-semibold mb-3">Legal</div>
            <ul className="space-y-2">
              <li><Link to="/terms" className="hover:text-foreground">Terms</Link></li>
              <li><Link to="/refunds" className="hover:text-foreground">Refunds</Link></li>
              <li><Link to="/privacy" className="hover:text-foreground">Privacy</Link></li>
            </ul>
          </div>
        </div>
        <p className="text-center text-muted-foreground/70">
          Payments handled by Paddle.com (our Merchant of Record).
        </p>
      </footer>
    </div>
  );
}